// ============================================================
// Datei: src/components/deploymentShiftTabs/TimelineShiftOverview.jsx
//
// PURPOSE:
// - Aufbau und Abbau werden gleich dargestellt wie Festival
// - horizontale Tabelle:
//   links Ressort, danach Tages-Spalten
// - Helfer können sich direkt für öffentliche Schichten einschreiben
//
// ÄNDERUNGEN:
// - Ressort-Info-Icon für Aufbau und Abbau ergänzt
// - Klick auf Ressort-Kachel oder Info-Icon lädt Ressortbeschreibung
// - RessortDescriptionModal ergänzt
// - Beschreibung wird über /departments/{department}/descriptions geladen
// - bestehende Schichtlogik unverändert gelassen
// ============================================================

import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";

import Api from "../../Api";
import RessortDescriptionModal from "../modals/RessortDescriptionModal";

import "../shiftOverview/shiftOverview.css";
import "./deploymentShiftTabs.css";

const MAX_ASSIGNED_SHIFTS = 3;

const DAY_ORDER_AUFBAU = [
	"Samstag",
	"Sonntag",
	"Montag",
	"Dienstag",
	"Mittwoch",
	"Donnerstag",
	"Freitag"
];

const DAY_ORDER_ABBAU = [
	"Sonntag",
	"Montag",
	"Dienstag",
	"Mittwoch",
	"Donnerstag",
	"Freitag",
	"Samstag"
];

const FALLBACK_DEPARTMENT_LABEL = "Ohne Ressort";

const TimelineShiftOverview = ({
	title,
	phase,
	festivalStartDate,
	shifts = [],
	assignedShifts = [],
	handleJoinShift
}) => {

	// ============================================================
	// STATE FÜR RESSORTBESCHREIBUNG
	//
	// activeDept:
	// - enthält den Namen des Ressorts, für das das Modal offen ist
	//
	// ressortDescriptions:
	// - enthält die Beschreibungstexte aus der Datenbank
	// ============================================================
	const [activeDept, setActiveDept] = useState(null);
	const [ressortDescriptions, setRessortDescriptions] = useState([]);

	// ============================================================
	// HELPER: Text vereinheitlichen
	// Damit Vergleiche robust bleiben.
	// ============================================================
	const normalizeText = (value) => {
		return String(value || "")
			.trim()
			.toUpperCase()
			.replaceAll("Ä", "AE")
			.replaceAll("Ö", "OE")
			.replaceAll("Ü", "UE");
	};

	// ============================================================
	// HELPER: Zeit ohne Sekunden anzeigen
	// Beispiel: "09:00:00" -> "09:00"
	// ============================================================
	const formatTime = (time) => {
		if (!time) {
			return "--:--";
		}

		return String(time).substring(0, 5);
	};

	// ============================================================
	// HELPER: Zeit in Minuten umrechnen
	// Wird für Sortierung und Überschneidungen verwendet.
	// ============================================================
	const toMinutes = (time) => {
		if (!time) {
			return 0;
		}

		const [hours = 0, minutes = 0] = String(time)
			.split(":")
			.map(Number);

		return hours * 60 + minutes;
	};

	// ============================================================
	// HELPER: Sicheres Date-Objekt erstellen
	//
	// Wichtig:
	// "YYYY-MM-DDT00:00:00" verhindert Zeitzonenverschiebungen,
	// die sonst bei new Date("YYYY-MM-DD") auftreten können.
	// ============================================================
	const createDateFromString = (dateString) => {
		if (!dateString) {
			return null;
		}

		const date = new Date(`${dateString}T00:00:00`);

		if (Number.isNaN(date.getTime())) {
			return null;
		}

		return date;
	};

	// ============================================================
	// HELPER: Tage addieren oder abziehen
	// ============================================================
	const addDays = (date, amount) => {
		const result = new Date(date);
		result.setDate(result.getDate() + amount);
		return result;
	};

	// ============================================================
	// HELPER: Datum schön anzeigen
	// Beispiel: 08.08.2025
	// ============================================================
	const formatDate = (date) => {
		if (!date) {
			return "";
		}

		return date.toLocaleDateString("de-CH", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric"
		});
	};

	// ============================================================
	// HELPER: Tagesname aus DTO lesen
	// ============================================================
	const getDayLabel = (shift) => {
		return (
			shift.dayOfDeployment ||
			shift.deploymentDay ||
			shift.day ||
			shift.weekday ||
			"Unbekannt"
		);
	};

	// ============================================================
	// HELPER: Department aus DTO lesen
	// ============================================================
	const getDepartmentLabel = (shift) => {
		return (
			shift.department ||
			shift.departmentName ||
			shift.ressort ||
			FALLBACK_DEPARTMENT_LABEL
		);
	};

	// ============================================================
	// HELPER: CSS-sicheren Department-Namen erzeugen
	//
	// Beispiel:
	// "Umwelt & Ambiente" -> "UmweltAmbiente"
	// ============================================================
	const getDepartmentClassName = (department) => {
		return String(department || FALLBACK_DEPARTMENT_LABEL)
			.replaceAll("&", "")
			.replaceAll(" ", "")
			.replaceAll("-", "")
			.replaceAll("ü", "ue")
			.replaceAll("Ü", "Ue")
			.replaceAll("ä", "ae")
			.replaceAll("Ä", "Ae")
			.replaceAll("ö", "oe")
			.replaceAll("Ö", "Oe");
	};

	// ============================================================
	// HELPER: Phase aus DTO lesen
	//
	// Dein Backend sendet:
	// phase: "Aufbau" | "Festival" | "Abbau"
	// ============================================================
	const getPhaseValue = (shift) => {
		return (
			shift.phase ||
			shift.shiftPhase ||
			shift.deploymentPhase ||
			shift.period ||
			shift.weekType ||
			shift.shiftCategory ||
			shift.category ||
			""
		);
	};

	// ============================================================
	// HELPER: Prüft, ob Schicht zur gewünschten Phase gehört
	// ============================================================
	const isShiftInPhase = (shift) => {
		const wantedPhase = normalizeText(phase);
		const currentPhase = normalizeText(getPhaseValue(shift));

		return currentPhase === wantedPhase;
	};

	// ============================================================
	// HELPER: Prüft, ob Schicht öffentlich sichtbar ist
	//
	// Falls shiftVisibility fehlt, wird die Schicht trotzdem gezeigt.
	// ============================================================
	const isPublicShift = (shift) => {
		if (!shift.shiftVisibility) {
			return true;
		}

		return normalizeText(shift.shiftVisibility) === "PUBLIC";
	};

	// ============================================================
	// HELPER: verfügbare Plätze berechnen
	//
	// Priorität:
	// 1. availableSlots
	// 2. publicSlots
	// 3. requestedAmountOfVolunteers - bookedShift
	// ============================================================
	const getAvailableSlots = (shift) => {
		if (shift.availableSlots !== undefined && shift.availableSlots !== null) {
			return Math.max(0, Number(shift.availableSlots));
		}

		if (shift.publicSlots !== undefined && shift.publicSlots !== null) {
			return Math.max(0, Number(shift.publicSlots));
		}

		const requested = Number(shift.requestedAmountOfVolunteers || 0);
		const booked = Number(shift.bookedShift || 0);

		return Math.max(0, requested - booked);
	};

	// ============================================================
	// HELPER: Tagesreihenfolge je nach Phase bestimmen
	// ============================================================
	const getDaysForPhase = () => {
		return normalizeText(phase) === "ABBAU"
			? DAY_ORDER_ABBAU
			: DAY_ORDER_AUFBAU;
	};

	// ============================================================
	// HELPER: Datum pro Tag berechnen
	//
	// Grundlage:
	// festivalStartDate = Festival-Freitag
	//
	// Aufbau:
	// Samstag = Festival-Freitag - 6 Tage
	// Sonntag = Festival-Freitag - 5 Tage
	// ...
	// Freitag = Festival-Freitag
	//
	// Abbau:
	// Sonntag = Festival-Freitag + 2 Tage
	// Montag = Festival-Freitag + 3 Tage
	// ...
	// Samstag = Festival-Freitag + 8 Tage
	// ============================================================
	const getDateForDay = (day) => {
		const startDate =
			createDateFromString(festivalStartDate) ||
			createDateFromString("2026-06-19");

		if (!startDate) {
			return "";
		}

		const normalizedPhase = normalizeText(phase);
		const days = getDaysForPhase();
		const dayIndex = days.findIndex((currentDay) =>
			normalizeText(currentDay) === normalizeText(day)
		);

		if (dayIndex === -1) {
			return "";
		}

		if (normalizedPhase === "ABBAU") {
			return formatDate(addDays(startDate, 2 + dayIndex));
		}

		return formatDate(addDays(startDate, -6 + dayIndex));
	};

	// ============================================================
	// HELPER: Tagesindex für Sortierung
	// ============================================================
	const getDayIndex = (dayLabel) => {
		const days = getDaysForPhase();
		const normalizedDay = normalizeText(dayLabel);

		const index = days.findIndex((day) =>
			normalizedDay === normalizeText(day)
		);

		return index === -1 ? 999 : index;
	};

	// ============================================================
	// RESSORTBESCHREIBUNG LADEN
	//
	// Wird aufgerufen, wenn auf die Ressort-Kachel oder das Info-Icon
	// geklickt wird.
	//
	// Beispiel:
	// - /departments/Hang/descriptions
	// - /departments/Infrastruktur/descriptions
	// ============================================================
	const handleInfoClick = async (department) => {
		try {
			const response = await Api.get(
				`/departments/${department}/descriptions`
			);

			setRessortDescriptions(Array.isArray(response.data) ? response.data : []);
			setActiveDept(department);

		} catch (error) {
			console.error(
				`Fehler beim Laden der Ressortbeschreibung für ${department}:`,
				error
			);

			// Modal trotzdem öffnen, damit sichtbar ist,
			// dass keine Beschreibung geladen werden konnte.
			setRessortDescriptions([]);
			setActiveDept(department);
		}
	};

	// ============================================================
	// RESSORTBESCHREIBUNG SCHLIESSEN
	// ============================================================
	const closeDescriptionModal = () => {
		setActiveDept(null);
		setRessortDescriptions([]);
	};

	// ============================================================
	// HELPER: Prüft, ob die Schicht mit einer bereits übernommenen
	// Schicht kollidiert.
	// ============================================================
	const isTimeBlocked = (shift) => {
		const currentPhase = normalizeText(getPhaseValue(shift));
		const currentDay = normalizeText(getDayLabel(shift));
		const currentStart = toMinutes(shift.startTime);
		let currentEnd = toMinutes(shift.endTime);

		if (currentEnd <= currentStart) {
			currentEnd += 1440;
		}

		return assignedShifts.some((assigned) => {
			const assignedPhase = normalizeText(getPhaseValue(assigned));
			const assignedDay = normalizeText(getDayLabel(assigned));

			if (assignedPhase !== currentPhase || assignedDay !== currentDay) {
				return false;
			}

			const assignedStart = toMinutes(assigned.startTime);
			let assignedEnd = toMinutes(assigned.endTime);

			if (assignedEnd <= assignedStart) {
				assignedEnd += 1440;
			}

			return currentStart < assignedEnd && assignedStart < currentEnd;
		});
	};

	// ============================================================
	// Schichten für Aufbau/Abbau filtern und sortieren
	// ============================================================
	const phaseShifts = useMemo(() => {
		return shifts
			.filter(isPublicShift)
			.filter(isShiftInPhase)
			.sort((a, b) => {
				const dayDiff =
					getDayIndex(getDayLabel(a)) -
					getDayIndex(getDayLabel(b));

				if (dayDiff !== 0) {
					return dayDiff;
				}

				return toMinutes(a.startTime) - toMinutes(b.startTime);
			});
	}, [shifts, phase]);

	// ============================================================
	// Departments sammeln
	// Jede Zeile der Tabelle entspricht einem Ressort.
	// ============================================================
	const departments = useMemo(() => {
		const uniqueDepartments = new Set();

		phaseShifts.forEach((shift) => {
			uniqueDepartments.add(getDepartmentLabel(shift));
		});

		return Array.from(uniqueDepartments).sort((a, b) =>
			a.localeCompare(b, "de-CH")
		);
	}, [phaseShifts]);

	// ============================================================
	// Schichten nach Department und Tag gruppieren
	//
	// Struktur:
	// {
	//   Infrastruktur: {
	//     Montag: [shift1, shift2]
	//   }
	// }
	// ============================================================
	const shiftsByDepartmentAndDay = useMemo(() => {
		return phaseShifts.reduce((groups, shift) => {
			const department = getDepartmentLabel(shift);
			const day = getDayLabel(shift);

			if (!groups[department]) {
				groups[department] = {};
			}

			if (!groups[department][day]) {
				groups[department][day] = [];
			}

			groups[department][day].push(shift);

			return groups;
		}, {});
	}, [phaseShifts]);

	const days = getDaysForPhase();
	const maxShiftsReached = assignedShifts.length >= MAX_ASSIGNED_SHIFTS;

	return (
		<div className="phase-shift-overview">
			<h3 className="phase-shift-title">{title}</h3>

			{phaseShifts.length === 0 && (
				<div className="timeline-empty-card">
					<p>
						Zurzeit sind keine öffentlichen Schichten für diesen Bereich verfügbar.
					</p>
				</div>
			)}

			{phaseShifts.length > 0 && (
				<div className="phase-table-scroll">

					{/* ============================================================
					    HEADER:
					    Links Ressort, danach alle Tage horizontal.
					   ============================================================ */}
					<div className="phase-table-header">
						<div className="header-cell ressort">
							Ressort
						</div>

						{days.map((day) => (
							<div
								key={day}
								className={`header-cell phase-day-header day-${day}`}
							>
								<span className="phase-day-name">
									{day}
								</span>

								<span className="phase-day-date">
									{getDateForDay(day)}
								</span>
							</div>
						))}
					</div>

					{/* ============================================================
					    BODY:
					    Jede Zeile ist ein Department.
					   ============================================================ */}
					{departments.map((department) => {
						const departmentClassName = getDepartmentClassName(department);

						return (
							<div
								key={department}
								className="phase-table-row"
							>
								{/* ====================================================
								    RESSORT-KACHEL
								    Neu:
								    - Info-Icon sichtbar
								    - Klick lädt Beschreibung aus der Datenbank
								   ==================================================== */}
								<div
									className={`dept-label dept-${departmentClassName}`}
									onClick={() => handleInfoClick(department)}
								>
									<img
										src={`${process.env.PUBLIC_URL}/icons/icon_info.svg`}
										className="dept-info-icon"
										alt="info"
										onClick={(event) => {
											event.stopPropagation();
											handleInfoClick(department);
										}}
									/>

									{department}
								</div>

								{days.map((day) => {
									const dayShifts =
										shiftsByDepartmentAndDay[department]?.[day] || [];

									return (
										<div
											key={`${department}-${day}`}
											className="phase-shift-cell"
										>
											{dayShifts.map((shift) => {
												const available = getAvailableSlots(shift);
												const isFull = available <= 0;

												const disabled =
													isFull ||
													maxShiftsReached ||
													isTimeBlocked(shift);

												return (
													<div
														key={shift.shiftId}
														className={`shift-card dept-${departmentClassName} day-${day} ${disabled ? "disabled" : ""}`}
													>
														<div className="shift-info">
															<p>
																{formatTime(shift.startTime)} – {formatTime(shift.endTime)}
															</p>

															<p>
																Verfügbar: {available}
															</p>
														</div>

														<button
															type="button"
															className="join-button"
															disabled={disabled}
															onClick={() => handleJoinShift(shift.shiftId)}
														>
															{isFull ? "Voll" : "Einschreiben"}
														</button>
													</div>
												);
											})}
										</div>
									);
								})}
							</div>
						);
					})}
				</div>
			)}

			{/* ============================================================
			    RESSORTBESCHREIBUNG MODAL
			   ============================================================ */}
			{activeDept &&
				createPortal(
					<RessortDescriptionModal
						department={activeDept}
						descriptions={ressortDescriptions}
						onClose={closeDescriptionModal}
					/>,
					document.body
				)}
		</div>
	);
};

export default TimelineShiftOverview;